const generatedCanonical = [];
if (typeof window !== 'undefined') {
    window.generatedCanonical = generatedCanonical;
}
export function generateCanonicalAddressWithDebug(response) {
    const canonicalAddress = generateCanonicalAddress(response);
    if (typeof window !== 'undefined' && window.localStorage._debug_location) {
        const debug = { address_components: response.address_components, canonicalAddress };
        console.log(debug);
        generatedCanonical.push(debug);
    }
    return canonicalAddress;
}
export function generateCanonicalAddress(response) {
    const components = response.address_components;
    // Helper function to find component by type
    const findComponentByType = (preferredTypes) => {
        for (const type of preferredTypes) {
            const match = components.find((c) => c.types.includes(type));
            if (match)
                return match;
        }
        return undefined;
    };
    // Helper function to find establishment/natural_feature that could serve as place name
    const findPlaceEstablishment = () => {
        return components.find((c) => (c.types.includes('establishment') || c.types.includes('natural_feature')) &&
            !c.types.includes('point_of_interest'));
    };
    // Helper function to detect significant administrative areas by naming patterns
    const hasSignificantAdminTitle = (name) => {
        // Match specific administrative titles that should be preserved
        // Note: We're more selective with "District" - only when it's the primary entity, not a subdividing district
        return (/^(Royal Borough|Metropolitan Borough|London Borough of)/.test(name) ||
            /^City of (Westminster|Leeds|London)$/.test(name));
    };
    // Helper function to detect significant neighborhoods by structure
    const isNeighborhoodSignificant = (neighborhoodName, postalTownName, components) => {
        if (!neighborhoodName)
            return false;
        // Find the neighborhood component to check its structure
        const neighborhoodComponent = components.find((c) => c.long_name === neighborhoodName &&
            c.types.some((t) => ['neighborhood', 'sublocality', 'sublocality_level_1', 'administrative_area_level_4'].includes(t)));
        if (!neighborhoodComponent)
            return false;
        // Special case: If neighborhood is at administrative_area_level_4 in London, it's usually not significant
        // (these are typically small administrative divisions that should collapse to postal_town)
        if (neighborhoodComponent.types.includes('administrative_area_level_4') && postalTownName === 'London') {
            return false;
        }
        // Filter out obviously generic/test areas
        if (/^(Some Generic|Test|Example)/i.test(neighborhoodName)) {
            return false;
        }
        // If neighborhood differs from postal_town AND has political + sublocality significance, it's likely significant
        const hasSignificantTypes = neighborhoodComponent.types.includes('political') &&
            neighborhoodComponent.types.some((t) => ['sublocality', 'sublocality_level_1'].includes(t));
        return hasSignificantTypes && neighborhoodName !== postalTownName;
    };
    // Extract place (locality, sublocality, or city)
    const locality = findComponentByType(['locality'])?.long_name;
    const postalTown = findComponentByType(['postal_town'])?.long_name;
    const neighborhood = findComponentByType([
        'neighborhood',
        'sublocality',
        'sublocality_level_1',
        'administrative_area_level_4',
    ])?.long_name;
    const administrativeArea = findComponentByType([
        'administrative_area_level_3',
        'administrative_area_level_2',
    ])?.long_name;
    const establishment = findPlaceEstablishment()?.long_name;
    // Extract region (state, province, or administrative area) - prioritize level_1, then level_2
    const region = findComponentByType(['administrative_area_level_1'])?.long_name ||
        findComponentByType(['administrative_area_level_2'])?.long_name;
    // Extract country
    const country = findComponentByType(['country'])?.long_name;
    // Special handling for UK
    let place;
    if (country === 'United Kingdom') {
        // Check if it's City of London (special case - square mile)
        const isCityOfLondon = neighborhood === 'City of London';
        // Check if administrative area has significant title that should be preserved
        const isSignificantAdmin = administrativeArea && hasSignificantAdminTitle(administrativeArea);
        // Check if neighborhood is significant based on structure
        const isSignificantNeighborhood = isNeighborhoodSignificant(neighborhood, postalTown, components);
        if (isCityOfLondon) {
            // City of London (square mile) should preserve its distinct identity
            place = neighborhood;
        }
        else if (isSignificantAdmin) {
            // Preserve Royal Borough and other significant administrative names
            place = administrativeArea;
        }
        else if (isSignificantNeighborhood) {
            // Use neighborhood for well-known areas that are distinct destinations
            place = neighborhood;
        }
        else {
            // UK prioritizes postal_town over locality for standard addresses
            // This handles cases like Manchester where both locality and postal_town exist
            place = postalTown || locality || neighborhood || administrativeArea;
        }
    }
    // Special handling for Singapore
    else if (country === 'Singapore') {
        // Case 1: When locality equals country and neighborhood exists, use neighborhood
        if (locality === 'Singapore' && neighborhood) {
            place = neighborhood;
        }
        // Case 2: When locality is "Singapura" (Malay variant), use neighborhood if available
        else if (locality === 'Singapura' && neighborhood) {
            place = neighborhood;
        }
        // Case 3: No locality but has neighborhood (like Sentosa case)
        else if (!locality && neighborhood) {
            place = neighborhood;
        }
        // Case 4: No neighborhood but has meaningful establishment (like Marina Bay)
        else if (!neighborhood && establishment && locality === 'Singapore') {
            place = establishment;
        }
        // Case 5: Fallback to normal priority
        else {
            place = locality || neighborhood || administrativeArea;
        }
    }
    else {
        // Non-UK/Singapore addresses use normal priority
        place = locality || postalTown || neighborhood || administrativeArea;
    }
    // Build canonical address, filtering out undefined values
    const addressParts = [place, region, country].filter(Boolean);
    if (!addressParts.length) {
        // Fallback to formatted address if no components found
        return response.formatted_address;
    }
    // Probably a country only location
    if (components.length === 1) {
        return [...new Set(addressParts)].join(', ');
    }
    return addressParts.join(', ');
}
